/**
 * Registration API Service
 *
 * Submits registration to the legacy sistema.bsports.cl endpoint
 * (usuario/registroUsuarioApp). Calls bsports directly — Amplify does not
 * run Netlify Functions, and bsports already allows CORS from
 * https://www.btalentstar.com.
 */

export interface RegistrationPayload {
  nombres: string;
  apellidoPaterno: string;
  apellidoMaterno: string;
  pais: string;
  posicion: string;
  fechaNacimiento: string;
  genero: number;
  perfilPredominante: number;
  tutorNombre: string;
  tutorTelefono: string;
  tutorEmail: string;
  email: string;
  clave: string;
  claveConfirm: string;
  acceptTerms: boolean;
  lenguaje: number;
}

export interface RegistrationResult {
  /** Legacy user id returned as a plain number on success. */
  userId: string;
}

const LEGACY_REGISTER_URL =
  (import.meta.env.VITE_LEGACY_API_URL || 'https://sistema.bsports.cl/index.php?r=') +
  'usuario/registroUsuarioApp';

/**
 * Builds the JSON payload matching the legacy registroUsuarioApp endpoint.
 */
function buildLegacyPayload(data: RegistrationPayload) {
  const [year, month, day] = data.fechaNacimiento.split('-');

  return {
    fechaNacimiento: `${year}/${parseInt(month, 10)}/${parseInt(day, 10)}`,
    nomUsuario: data.nombres.trim(),
    apePatUsuario: data.apellidoPaterno.trim(),
    apeMatUsuario: data.apellidoMaterno.trim() || '',
    email: data.email.trim().toLowerCase(),
    clave: data.clave.trim(),
    rut: '',
    genero: data.genero,
    pais: parseInt(data.pais, 10),
    comuna: 0,
    nacionalidad: '',
    region: 0,
    direccion: '',
    estatura: 0,
    posicion: parseInt(data.posicion, 10),
    perfil_predominante: data.perfilPredominante,
    peso: 0,
    nomApo: data.tutorNombre.trim(),
    telApo: data.tutorTelefono.trim(),
    emailApo: data.tutorEmail.trim(),
    juvenil: null,
    desc_juvenil: '',
    dnacionalidad: null,
    desc_dnacionalidad: '',
    aceptaTerminos: data.acceptTerms ? 1 : 0,
    cupon: '',
    cuponValid: 0,
    lenguaje: data.lenguaje,
    plan: '',
  };
}

function mapLegacyError(errorKey: string): string {
  if (errorKey === 'rutEmail' || errorKey === 'email') {
    return 'Este email ya está registrado.';
  }
  if (errorKey === 'rut') {
    return 'Este RUT ya está registrado.';
  }
  return `${errorKey}. Por favor intenta nuevamente o contacta a contact@btalentstar.com`;
}

/**
 * Submits the registration to the legacy bsports backend.
 * On success returns the new user id.
 */
export async function submitRegistration(
  data: RegistrationPayload,
): Promise<RegistrationResult> {
  const payload = buildLegacyPayload(data);

  let response: Response;
  try {
    response = await fetch(LEGACY_REGISTER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Credentialed so bsports can set the session cookie used by the
      // subscription / payment page after we redirect.
      credentials: 'include',
      body: JSON.stringify(payload),
    });
  } catch {
    throw new Error(
      'No se pudo conectar con el servidor. Por favor intenta nuevamente o contacta a contact@btalentstar.com',
    );
  }

  const raw = (await response.text()).trim();

  // Amplify SPA fallback used to return index.html with 200 — reject that.
  // Note: bsports itself often sets Content-Type: text/html even for plain
  // text/JSON bodies, so we must inspect the body, not the header.
  if (raw.startsWith('<!DOCTYPE') || raw.startsWith('<html') || raw.startsWith('<HTML')) {
    throw new Error(
      'El servicio de registro no está disponible. Por favor intenta nuevamente o contacta a contact@btalentstar.com',
    );
  }

  if (!response.ok) {
    let errorKey = raw;
    try {
      const parsed = JSON.parse(raw);
      errorKey = parsed?.error || parsed?.messaje || parsed?.message || raw;
    } catch {
      /* keep raw */
    }
    throw new Error(mapLegacyError(String(errorKey)));
  }

  // Success: legacy returns a plain numeric user id (e.g. "32852").
  // Error strings like "email" / "rutEmail" also come back as 200 sometimes.
  if (/^\d+$/.test(raw)) {
    return { userId: raw };
  }

  // Known business errors returned as plain text / JSON with 200
  let errorKey = raw;
  try {
    const parsed = JSON.parse(raw);
    errorKey = parsed?.error || parsed?.messaje || parsed?.message || raw;
  } catch {
    /* keep raw */
  }
  throw new Error(mapLegacyError(String(errorKey)));
}
